import { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import EquipmentSection from './EquipmentSection';
import InventorySection from './InventorySection';
import ForgeSection from './ForgeSection';
import MerchantModal from './MerchantModal';
import CharacterStatsModal from './CharacterStatsModal';
import type { Character } from '../../auth/authApi';
import styles from './PlayerToolbar.module.css';

const profileIcon = '/icons/profile.svg';
const marketIcon = '/icons/market.svg';
const equipmentIcon = '/icons/equipment.svg';
const backpackIcon = '/icons/backpack.svg';

// ─────────────────────────────────────────────────────────────────────────────
// PlayerToolbar — controles do jogador.
//  • variant='inline'  → dashboard: Equipamento · Mochila · Forja · Mercado
//  • variant='compact' → Hub: só ícones pequenos de Equipamento · Mochila ·
//    Ficha (Forja e Mercado já têm NPC no mapa).
// A configuração da Barra de Batalha vive dentro da Ficha do Personagem.
// ─────────────────────────────────────────────────────────────────────────────

interface PlayerToolbarProps {
  token: string;
  character: Character;
  classColor?: string;
  onCharacterUpdate?: (c: Character) => void;
  variant?: 'inline' | 'compact';
  /** Mostra o botão da Ficha. No dashboard 'inline' (false) o card já abre. */
  showFicha?: boolean;
  /** variant='compact': as janelas encaixam nesse `top` (abaixo do painel). */
  dockTop?: number;
}

export default function PlayerToolbar({
  token,
  character,
  classColor = '#c8a040',
  onCharacterUpdate,
  variant = 'inline',
  showFicha = true,
  dockTop,
}: PlayerToolbarProps) {
  const { t } = useTranslation();
  const [showShop, setShowShop] = useState(false);
  const [showStats, setShowStats] = useState(false);

  const compact = variant === 'compact';

  // ── Hub: barra compacta — UMA janela por vez (mutuamente exclusivas) ─────
  const [panel, setPanel] = useState<'equip' | 'inv' | 'ficha' | null>(null);
  if (compact) {
    const toggle = (p: 'equip' | 'inv' | 'ficha') => setPanel(cur => (cur === p ? null : p));
    return (
      <div className={styles.compactBar}>
        <button
          className={`${styles.compactIcon} ${panel === 'equip' ? styles.compactIconOn : ''}`}
          onClick={() => toggle('equip')}
          title={t('equipment.title' as any)}
        ><img src={equipmentIcon} alt="" /></button>

        <button
          className={`${styles.compactIcon} ${panel === 'inv' ? styles.compactIconOn : ''}`}
          onClick={() => toggle('inv')}
          title={t('inventory.title' as any)}
        ><img src={backpackIcon} alt="" /></button>

        <button
          className={`${styles.compactIcon} ${panel === 'ficha' ? styles.compactIconOn : ''}`}
          onClick={() => toggle('ficha')}
          title={t('profile.title' as any) || 'Ficha do Personagem'}
        >
          <img src={profileIcon} alt="" />
          {!!character.unspentAttributePoints && character.unspentAttributePoints > 0 && (
            <span className={styles.alert}>!</span>
          )}
        </button>

        <EquipmentSection token={token} dock dockTop={dockTop}
          controlled={{ open: panel === 'equip', onClose: () => setPanel(null) }} />
        <InventorySection token={token} dock dockTop={dockTop}
          controlled={{ open: panel === 'inv', onClose: () => setPanel(null) }} />
        {panel === 'ficha' && (
          <CharacterStatsModal
            character={character}
            classColor={classColor}
            token={token}
            dock
            dockTop={dockTop}
            onClose={() => setPanel(null)}
            onUpdate={onCharacterUpdate}
          />
        )}
      </div>
    );
  }

  const statsModal = showStats && (
    <CharacterStatsModal
      character={character}
      classColor={classColor}
      token={token}
      onClose={() => setShowStats(false)}
      onUpdate={onCharacterUpdate}
    />
  );

  // ── Dashboard: controles grandes com rótulo ─────────────────────────────
  return (
    <div className={styles.bar}>
      <EquipmentSection token={token} />
      <InventorySection token={token} />
      <ForgeSection token={token} />

      <button className={styles.tile} onClick={() => setShowShop(true)} title={t('merchant.title' as any)}>
        <span className={styles.tileImgWrap}><img src={marketIcon} alt="" className={styles.tileImg} /></span>
        <span className={styles.tileLabel}>{t('merchant.title' as any)}</span>
      </button>

      {showFicha && (
        <button className={styles.tile} onClick={() => setShowStats(true)} title={t('profile.title' as any)}>
          <span className={styles.tileImgWrap}>
            <img src={profileIcon} alt="" className={styles.tileImg} />
            {!!character.unspentAttributePoints && character.unspentAttributePoints > 0 && (
              <span className={styles.alert}>!</span>
            )}
          </span>
          <span className={styles.tileLabel}>{t('profile.title' as any)}</span>
        </button>
      )}

      {statsModal}
      {showShop && <MerchantModal token={token} onClose={() => setShowShop(false)} />}
    </div>
  );
}
